import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, getActiveBranchName } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ShareExportPanel } from "@/components/reports/ShareExportPanel";
import { ApStatusBadge, AgingBadge } from "@/components/ap/ApStatusBadge";
import { money } from "@/lib/constants";
import { Search, Filter, Receipt, Wallet, AlertTriangle, CheckCircle2 } from "lucide-react";

const AP_STATUSES = [
  { value: "pending", label: "Pendiente" },
  { value: "partial", label: "Parcial" },
  { value: "paid", label: "Pagada" },
  { value: "cancelled", label: "Cancelada" },
];

const AGING_BUCKETS = [
  { value: "0-30", label: "0–30 días" },
  { value: "31-60", label: "31–60 días" },
  { value: "61-90", label: "61–90 días" },
  { value: "90+", label: "90+ días" },
];

export default function CuentasPorPagar() {
  const { hasPermission } = useAuth();
  const { isSuperAdmin, activeTenantId, branches } = useTenant();
  const navigate = useNavigate();
  const canQuery = !isSuperAdmin || !!activeTenantId;

  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterBranch, setFilterBranch] = useState("all");
  const [filterSupplier, setFilterSupplier] = useState("all");
  const [filterAging, setFilterAging] = useState("all");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");

  const tenantParam = isSuperAdmin && activeTenantId ? { tenant_id: activeTenantId } : {};
  const params = {
    ...tenantParam,
    search: search || undefined,
    status: filterStatus !== "all" ? filterStatus : undefined,
    branch_id: filterBranch !== "all" ? filterBranch : undefined,
    supplier_id: filterSupplier !== "all" ? filterSupplier : undefined,
    aging_bucket: filterAging !== "all" ? filterAging : undefined,
    date_from: filterDateFrom || undefined,
    date_to: filterDateTo || undefined,
  };

  async function fetchList() {
    if (!canQuery) { setItems([]); setLoading(false); return; }
    setLoading(true);
    try {
      const [{ data }, { data: sup }] = await Promise.all([
        api.get("/accounts-payable", { params }),
        api.get("/suppliers", { params: { ...tenantParam, include_inactive: true } }),
      ]);
      setItems(data);
      setSuppliers(sup);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(fetchList, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    search, filterStatus, filterBranch, filterSupplier, filterAging,
    filterDateFrom, filterDateTo, activeTenantId, isSuperAdmin,
  ]);

  const active = items.filter((a) => a.status !== "cancelled");
  const totalPending = active.filter((a) => a.status === "pending").reduce((s, a) => s + Number(a.balance_due || 0), 0);
  const totalPartial = active.filter((a) => a.status === "partial").reduce((s, a) => s + Number(a.balance_due || 0), 0);
  const totalPaid = active.reduce((s, a) => s + Number(a.total_paid_amount || 0), 0);
  const totalOverdue = active.filter((a) => (a.aging_bucket === "31-60" || a.aging_bucket === "61-90" || a.aging_bucket === "90+") && a.status !== "paid").length;

  return (
    <div className="space-y-6" data-testid="page-cuentas-por-pagar">
      <PageHeader
        title="Cuentas por pagar"
        description="Saldos con proveedores generados automáticamente desde las recepciones de OC."
        actions={
          <ShareExportPanel
            reportKey="accounts_payable"
            reportName="Cuentas por pagar"
            endpoint="/reports/accounts-payable"
            params={params}
            exportPermission="accounts_payable:export"
            filename="cuentas-por-pagar"
            branchId={filterBranch !== "all" ? filterBranch : null}
            filters={{
              date_from: filterDateFrom || null,
              date_to: filterDateTo || null,
              status: filterStatus !== "all" ? filterStatus : null,
              aging_bucket: filterAging !== "all" ? filterAging : null,
              branch_name: getActiveBranchName(),
            }}
            stats={{
              count: active.length,
              total_received_amount: active.reduce((s, a) => s + Number(a.total_received_amount || 0), 0),
              total_paid_amount: totalPaid,
              total_balance_due: totalPending + totalPartial,
              row_count: items.length,
            }}
            testIdPrefix="ap-share"
          />
        }
      />

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-border" data-testid="kpi-total-pending">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              <Wallet className="h-3.5 w-3.5" /> Pendiente
            </div>
            <div className="text-2xl font-semibold mono mt-1">{money(totalPending)}</div>
          </CardContent>
        </Card>
        <Card className="border-border" data-testid="kpi-total-partial">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              <Receipt className="h-3.5 w-3.5" /> Parcial (saldo)
            </div>
            <div className="text-2xl font-semibold mono mt-1">{money(totalPartial)}</div>
          </CardContent>
        </Card>
        <Card className="border-border" data-testid="kpi-total-paid">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              <CheckCircle2 className="h-3.5 w-3.5" /> Pagado (total)
            </div>
            <div className="text-2xl font-semibold mono mt-1">{money(totalPaid)}</div>
          </CardContent>
        </Card>
        <Card className="border-border" data-testid="kpi-total-overdue">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              <AlertTriangle className="h-3.5 w-3.5" /> Vencidas (&gt;30d)
            </div>
            <div className="text-2xl font-semibold text-amber-700 mt-1">{totalOverdue}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="bg-white border border-border rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
          <Filter className="h-3.5 w-3.5" /> Filtros
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="relative lg:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar folio OC…"
              className="pl-9 h-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="ap-search"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="ap-filter-status"><SelectValue placeholder="Estatus" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estatus</SelectItem>
              {AP_STATUSES.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={filterBranch} onValueChange={setFilterBranch}>
            <SelectTrigger data-testid="ap-filter-branch"><SelectValue placeholder="Sucursal" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Sucursal activa</SelectItem>
              {branches.map((b) => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={filterSupplier} onValueChange={setFilterSupplier}>
            <SelectTrigger data-testid="ap-filter-supplier"><SelectValue placeholder="Proveedor" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los proveedores</SelectItem>
              {suppliers.map((s) => (<SelectItem key={s.id} value={s.id}>{s.trade_name || s.legal_name}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={filterAging} onValueChange={setFilterAging}>
            <SelectTrigger data-testid="ap-filter-aging"><SelectValue placeholder="Antigüedad" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las antigüedades</SelectItem>
              {AGING_BUCKETS.map((a) => (<SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>))}
            </SelectContent>
          </Select>
          <div className="grid grid-cols-2 gap-2 lg:col-span-2">
            <Input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              data-testid="ap-filter-date-from"
            />
            <Input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              data-testid="ap-filter-date-to"
            />
          </div>
        </div>
      </div>

      <div className="text-xs text-muted-foreground" data-testid="ap-count">
        {items.length} cuenta{items.length === 1 ? "" : "s"}
      </div>

      <div className="bg-white border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : items.length === 0 ? (
          <EmptyState
            title="Sin cuentas por pagar"
            description="Las cuentas se generan automáticamente cuando registras una recepción de OC."
            icon={Wallet}
          />
        ) : (
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Folio OC</TableHead>
                <TableHead>Proveedor</TableHead>
                <TableHead>Sucursal</TableHead>
                <TableHead className="text-right">Recibido</TableHead>
                <TableHead className="text-right">Pagado</TableHead>
                <TableHead className="text-right">Saldo</TableHead>
                <TableHead>Antigüedad</TableHead>
                <TableHead>Estatus</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((ap) => (
                <TableRow
                  key={ap.id}
                  className="tbl-row cursor-pointer"
                  onClick={() => navigate(`/cuentas-por-pagar/${ap.id}`)}
                  data-testid={`row-ap-${ap.id}`}
                >
                  <TableCell className="mono font-medium text-tenant">{ap.po_folio}</TableCell>
                  <TableCell className="text-sm">{ap.supplier_name || "—"}</TableCell>
                  <TableCell className="text-sm">{ap.branch_name || "—"}</TableCell>
                  <TableCell className="text-right mono text-sm">{money(ap.total_received_amount)}</TableCell>
                  <TableCell className="text-right mono text-sm">{money(ap.total_paid_amount)}</TableCell>
                  <TableCell className="text-right mono text-sm font-semibold">{money(ap.balance_due)}</TableCell>
                  <TableCell><AgingBadge bucket={ap.aging_bucket} days={ap.age_days} /></TableCell>
                  <TableCell><ApStatusBadge status={ap.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
